import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentEditingField, selectShowHidden } from '../store/selectors';
import { AnimatePresence, motion } from 'framer-motion';
import { getFieldCount, isField, isTable } from '../store/utils';
import { calcLeftPadding } from '../utils';
import {
    WorksheetSchemaList,
    WorksheetSchemaListItem,
    WorksheetSchemaSection,
    WorksheetSchemaSectionGroup,
    WorksheetSchemaTable,
    WorksheetSchemaTableRow,
} from '../../../../types/custom-worksheet-v2';
import FieldItem from './FieldItem';
import NavItem from './NavItem';
import PropertyProgress from '../PropertyProgress';

type NonFieldItemProps = {
    section:
        | WorksheetSchemaSectionGroup
        | WorksheetSchemaSection
        | WorksheetSchemaList
        | WorksheetSchemaListItem
        | WorksheetSchemaTable
        | WorksheetSchemaTableRow;
};

function NonFieldItem({ section }: NonFieldItemProps): JSX.Element {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const currentEditingField = useSelector(selectCurrentEditingField);
    const showHiddenFields = useSelector(selectShowHidden);

    const { total, completed } = getFieldCount(section, showHiddenFields);

    return (
        <div>
            <div className="flex h-max items-stretch text-sm text-gray-600 hover:bg-gray-50">
                <div
                    className="flex py-1 pr-1 cursor-pointer group relative"
                    style={{ paddingLeft: calcLeftPadding(section.id) }}
                    onMouseDown={(e): void => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onClick={(): void => setIsCollapsed(!isCollapsed)}
                >
                    {!isTable(section) && (
                        <ChevronRightIcon
                            style={{ display: isTable(section) ? 'hidden' : 'inline-block' }}
                            className={`${
                                isCollapsed ? '' : 'rotate-90'
                            } group-hover:text-indigo-600 group-hover:scale-125 transition-all w-4 h-4 mt-0.5 shrink-0 absolute right-1`}
                        />
                    )}
                </div>
                <NavItem id={section.id}>
                    <div className="flex py-1 pl-1 pr-4 gap-2 group">
                        <p
                            className={`flex group-hover:text-indigo-600 ${
                                currentEditingField && currentEditingField.includes(section.id) ? 'font-bold' : ''
                            }`}
                        >
                            {`${section.name}`}
                        </p>
                        {completed !== total && (
                            <div className="ml-auto">
                                <PropertyProgress property={section} textPosition="right" />
                            </div>
                        )}
                    </div>
                </NavItem>
            </div>
            {!isTable(section) && (
                <AnimatePresence initial={false}>
                    {!isCollapsed && (
                        <motion.div
                            exit={{ height: 0, opacity: 0 }}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            transition={{ height: { duration: 0.2 } }}
                            className="overflow-hidden"
                        >
                            {Object.entries(section.properties)
                                .sort((a, b) => (a[1].order > b[1].order ? 1 : -1))
                                .map(([, value]) => {
                                    if (isField(value)) {
                                        return <FieldItem key={value.id} field={value} />;
                                    }
                                    return <NonFieldItem key={value.id} section={value} />;
                                })}
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </div>
    );
}

export default NonFieldItem;
